import { SectionalWritebackIllustration } from '@/assets/image/sectional-writeback';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { TypographyP } from '@heidi/design-system/typography';
import { sectionalWritebackInformModalOpenAtom } from '@heidi/integrations/ehr/data/atoms';
import { useAtom } from 'jotai';
import { ExternalLinkIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

const SectionalWritebackInformModal = memo(() => {
  const [informModalState, setInformModalState] = useAtom(
    sectionalWritebackInformModalOpenAtom
  );
  const { mutateAsync: updateUser } = useMutateUpdateUser();

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open) {
        // mark user as seen sectional writeback info based on action
        if (informModalState.from === 'template_editor') {
          await updateUser({
            has_shown_sectional_note_info_modal: true,
          });
        } else if (informModalState.from === 'push_note_to_ehr') {
          await updateUser({
            has_shown_push_sectional_note_instruction: true,
          });
        }
      }
      setInformModalState((prev) => ({ ...prev, open }));
    },
    [informModalState.from, setInformModalState, updateUser]
  );

  return (
    <Dialog
      open={informModalState.open}
      onOpenChange={(open) => void handleOpenChange(open)}
    >
      <DialogContent
        aria-describedby={undefined}
        hideCloseIcon
        className="flex max-w-[500px] flex-col items-center gap-6 p-6"
      >
        <SectionalWritebackIllustration
          className="w-full"
          alt="Sectional writeback illustration"
        />
        <div className="flex flex-col items-start gap-y-3">
          <Badge color="neutral" className="h-[22px] p-1.5 text-sm leading-[14px]">
            New!
          </Badge>
          <DialogTitle>Push content to different sections</DialogTitle>
          <TypographyP className="text-text-secondary">
            Hover over a block and click “Link to section,” then choose the desired
            section. The block will turn green on hover to confirm it’s linked.
          </TypographyP>
          <TypographyP className="text-text-secondary">
            When you click “Push” from the note, all linked blocks will go to their
            selected sections. If you add new content after generating, remember to link
            it as well so it can be pushed.
          </TypographyP>
        </div>
        <DialogFooter className="w-full justify-end">
          <DialogClose asChild>
            <Button size="lg" disableAutomaticSvgSizing variant="ghost">
              Dismiss
            </Button>
          </DialogClose>

          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.heidihealth.com/support/en/articles/10352786-athena-integration"
          >
            <Button size="lg" disableAutomaticSvgSizing>
              Learn more about section links
              <ExternalLinkIcon className="min-h-4 min-w-4" size={16} />
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

SectionalWritebackInformModal.displayName = 'SectionalWritebackInformModal';
export default SectionalWritebackInformModal;
